/**
 * DataLayer utility for Google Tag Manager integration
 * Handles page tracking and user information
 */

export interface UserData {
  user_id?: string;
  user_email?: string;
  user_firstname?: string;
  user_lastname?: string;
  user_street?: string;
  user_phone?: string;
  user_city?: string;
  user_region?: string;
  user_country?: string;
  user_zipcode?: string;
}

export interface DataLayerEvent {
  event: string;
  page_type: string;
  arbo: string;
  user_id?: string;
  user_email?: string;
  user_firstname?: string;
  user_lastname?: string;
  user_street?: string;
  user_phone?: string;
  user_city?: string;
  user_region?: string;
  user_country?: string;
  user_zipcode?: string;
}

/**
 * Initialize the dataLayer array if it doesn't exist
 */
export const initDataLayer = (): void => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
};

/**
 * Hash a string using SHA-256
 * @param str - String to hash
 * @returns Promise with the hashed string in hexadecimal format
 */
export const hashSHA256 = async (str: string): Promise<string> => {
  if (typeof window === "undefined" || !window.crypto?.subtle) {
    // Fallback for environments without crypto.subtle
    return str;
  }

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  } catch (error) {
    console.error("Error hashing string:", error);
    return str;
  }
};

/**
 * Get page type based on pathname
 * @param pathname - Current page pathname
 * @returns Page type string
 */
export const getPageType = (pathname: string): string => {
  if (pathname === "/") return "home";
  if (pathname.includes("/data") || pathname.includes("/ia")) return "category";
  if (pathname.includes("/projects/") || pathname.includes("/content/"))
    return "product";
  if (pathname.includes("/search")) return "searchresults";
  if (pathname.includes("/404")) return "errors";
  return "other";
};

/**
 * Get page arborescence based on pathname
 * @param pathname - Current page pathname
 * @returns Arborescence string with "/" separator
 */
export const getPageArbo = (pathname: string): string => {
  if (pathname === "/") return "home";

  // Remove leading and trailing slashes
  const cleanPath = pathname.replace(/^\/|\/$/g, "");

  // Build arborescence
  const segments = cleanPath.split("/").filter(Boolean);

  if (segments.length === 0) return "home";

  // Build hierarchical structure
  let arbo = "home";
  segments.forEach((segment) => {
    // Map specific pages to categories
    if (segment === "data" || segment === "ia") {
      arbo += "/category";
    } else if (segment === "projects" || segment === "content") {
      arbo += "/category";
    } else if (segment === "404") {
      arbo = "errors";
    } else {
      arbo += `/${segment}`;
    }
  });

  return arbo;
};

/**
 * Push dataLayer event with page and user information
 * Only includes populated variables (non-empty values)
 * @param pathname - Current page pathname
 * @param userData - Optional user data
 */
export const pushDataLayer = async (
  pathname: string,
  userData?: UserData,
): Promise<void> => {
  if (typeof window === "undefined") return;

  initDataLayer();

  const pageType = getPageType(pathname);
  const arbo = getPageArbo(pathname);

  // Build event object with only populated values
  const eventData: Partial<DataLayerEvent> = {
    event: "datalayer-loaded",
    page_type: pageType,
    arbo: arbo,
  };

  // Add user data only if available and not empty
  if (userData) {
    if (userData.user_id) {
      eventData.user_id = userData.user_id;
    }

    if (userData.user_email) {
      // Hash email with SHA-256
      eventData.user_email = await hashSHA256(userData.user_email);
    }

    if (userData.user_firstname) {
      eventData.user_firstname = userData.user_firstname;
    }

    if (userData.user_lastname) {
      eventData.user_lastname = userData.user_lastname;
    }

    if (userData.user_street) {
      eventData.user_street = userData.user_street;
    }

    if (userData.user_phone) {
      eventData.user_phone = userData.user_phone;
    }

    if (userData.user_city) {
      eventData.user_city = userData.user_city;
    }

    if (userData.user_region) {
      eventData.user_region = userData.user_region;
    }

    if (userData.user_country) {
      eventData.user_country = userData.user_country;
    }

    if (userData.user_zipcode) {
      eventData.user_zipcode = userData.user_zipcode;
    }
  }

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

/**
 * Get user data from your authentication system
 * This is a placeholder - replace with your actual user data retrieval logic
 * @returns User data or undefined if user is not logged in
 */
export const getUserData = (): UserData | undefined => {
  // TODO: Replace this with your actual user authentication logic
  // Example: Check localStorage, cookies, or authentication context

  if (typeof window === "undefined") return undefined;

  // Example implementation (replace with your actual logic):
  // const user = localStorage.getItem('user');
  // if (user) {
  //   const userData = JSON.parse(user);
  //   return {
  //     user_id: userData.id,
  //     user_email: userData.email,
  //     user_firstname: userData.firstname,
  //     user_lastname: userData.lastname,
  //     user_street: userData.street,
  //     user_phone: userData.phone,
  //     user_city: userData.city,
  //     user_region: userData.region,
  //     user_country: userData.country,
  //     user_zipcode: userData.zipcode,
  //   };
  // }

  return undefined;
};

/**
 * Push CTA click event to dataLayer
 * @param ctaName - Name/text of the CTA clicked
 * @param htmlZone - HTML zone where CTA was clicked (Header/Body/Footer)
 */
export const pushCTAClick = (ctaName: string, htmlZone: string): void => {
  if (typeof window === "undefined") return;

  initDataLayer();

  // Push to dataLayer
  window.dataLayer.push({
    event: "clic_cta",
    cta_name: ctaName,
    html_cta: htmlZone,
  });
};

/**
 * Push social network click event to dataLayer
 * @param socialNetwork - Name of the social network clicked
 */
export const pushSocialNetworkClick = (socialNetwork: string): void => {
  if (typeof window === "undefined") return;

  initDataLayer();

  // Push to dataLayer
  window.dataLayer.push({
    event: "clic_social_networks",
    social_network_clicked: socialNetwork,
  });
};

/**
 * Push contact form success event to dataLayer
 * @param formSubject - Subject of the contact form
 * @param userEmail - User email (will be hashed)
 */
export const pushContactFormSuccess = async (
  formSubject: string,
  userEmail: string,
): Promise<void> => {
  if (typeof window === "undefined") return;

  initDataLayer();

  // Build event object with only populated values
  const eventData: any = {
    event: "contact-form-success",
    form_subject: formSubject,
  };

  // Hash email with SHA-256
  if (userEmail) {
    eventData.user_email = await hashSHA256(userEmail);
  }

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

// Type declaration for window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}
